import { useCallback, useEffect, useMemo, useState } from 'react';
import type { TodayResponse, DailyEntry, MeasurementEntry, MeasurementMetric } from '@tally/lib/types';
import { mifflinBMR, stepKcal, NEAT_BASELINE_KCAL } from '@tally/lib/reconcile';
import { todayBangkok } from '@tally/lib/date';
import { getToday, triggerSync, type SyncSummary } from './lib/api.js';
import { SectionHead } from './components/SectionHead.js';
import { Stat, type Tone } from './components/Stat.js';
import { TodayEditor } from './components/TodayEditor.js';
import { Reality } from './components/Reality.js';
import { LearnedBMR } from './components/LearnedBMR.js';
import { ChartCard, DualChart, MeasurementChartCard } from './components/Charts.js';
import { SourceCard } from './components/SourceCard.js';
import { CronometerFallback } from './components/CronometerFallback.js';
import { HeroMeasurement } from './components/HeroMeasurement.js';
import { HighlightedStat } from './components/HighlightedStat.js';
import { MeasurementInline } from './components/MeasurementInline.js';

interface MetricSpec {
  metric: MeasurementMetric;
  label: string;
  color: string;
}
const MEASUREMENT_METRICS: MetricSpec[] = [
  { metric: 'waist',     label: 'Waist',     color: '#fcd34d' },
  { metric: 'chest',     label: 'Chest',     color: '#a7f3d0' },
  { metric: 'shoulders', label: 'Shoulders', color: '#fda4af' },
];

function latestByMetric(log: MeasurementEntry[], metric: MeasurementMetric): number | null {
  for (let i = log.length - 1; i >= 0; i--) {
    if (log[i].metric === metric) return log[i].value_cm;
  }
  return null;
}

function filterByMetric(log: MeasurementEntry[], metric: MeasurementMetric): MeasurementEntry[] {
  return log.filter((e) => e.metric === metric);
}

const KCAL_PER_KG = 7700;

const BROWSER_BANGKOK_TODAY = todayBangkok();

export default function App() {
  const [data, setData] = useState<TodayResponse | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState<SyncSummary | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const d = await getToday();
      setData(d);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    setSyncing(true);
    try {
      // fresh=1 forces a brand-new Cronometer login + bypasses any cached state — ensures we get
      // any items added in the last few minutes.
      const result = await triggerSync({ fresh: true });
      setLastSync(result);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSyncing(false);
    }
  }, [load]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && !data) return <div className="p-6 text-stone-500 font-mono text-xs">loading…</div>;
  if (err && !data) return <div className="p-6 text-rose-400 font-mono text-xs">{err}</div>;
  if (!data || !data.today) return null;

  return <Dashboard data={data} editOpen={editOpen} setEditOpen={setEditOpen} reload={load} refresh={refresh} syncing={syncing} lastSync={lastSync} />;
}

function Dashboard({
  data,
  editOpen,
  setEditOpen,
  reload,
  refresh,
  syncing,
  lastSync,
}: {
  data: TodayResponse;
  editOpen: boolean;
  setEditOpen: (b: boolean) => void;
  reload: () => void;
  refresh: () => void;
  syncing: boolean;
  lastSync: SyncSummary | null;
}) {
  const today = data.today as DailyEntry;
  const recon = data.reconciliation;
  const profile = data.profile;
  const workoutKcalByDate = data.workout_kcal_by_date;

  const cronometerStatus = data.sync_status.find((s) => s.source === 'cronometer');
  const whoopStatus = data.sync_status.find((s) => s.source === 'whoop');
  const withingsStatus = data.sync_status.find((s) => s.source === 'withings');

  const chartData = useMemo(
    () =>
      data.recent.map((e) => ({
        date: e.date.slice(5),
        weight: e.weight_kg,
        bf: e.body_fat_pct,
        intake: e.calories_in,
        whoop: e.whoop_calories,
      })),
    [data.recent],
  );

  const isToday = today.date === BROWSER_BANGKOK_TODAY;
  const headingLabel = isToday ? `Daily Summary · Today · ${today.date}` : `Daily Summary · Yesterday · ${today.date}`;
  const editLabel = isToday ? (editOpen ? 'close' : 'edit today') : (editOpen ? 'close' : `edit ${today.date}`);

  // Bangkok-today's row pulled from `recent` (may have weight if weighed in, but typically no intake yet).
  const actualToday = data.recent.find((e) => e.date === BROWSER_BANGKOK_TODAY) ?? null;

  const fmtNum = (n: number | null | undefined, digits = 0) =>
    n == null ? '—' : digits === 0 ? Math.round(n).toString() : n.toFixed(digits);
  const sleepDisplay = today.sleep_hours != null
    ? `${Math.floor(today.sleep_hours)}h ${Math.round((today.sleep_hours % 1) * 60)}m`
    : '—';
  const recoveryTone: Tone = today.whoop_recovery == null ? 'default'
    : today.whoop_recovery >= 67 ? 'good' : today.whoop_recovery >= 34 ? 'warn' : 'bad';

  // ---- Trend calculations ----
  const mean = (xs: number[]) => (xs.length ? xs.reduce((s, x) => s + x, 0) / xs.length : null);
  const last7 = data.recent.slice(-7);

  // Weights — prefer last-7 vs prior-7 mean comparison; fall back to regression slope × 7 when <14 weigh-ins.
  const weightPoints = data.recent.filter((e) => e.weight_kg != null).map((e) => e.weight_kg as number);
  const avg7Weight = mean(weightPoints.slice(-7));
  const avg7prevWeight = weightPoints.length >= 14 ? mean(weightPoints.slice(-14, -7)) : null;
  const meanCompareDelta = avg7Weight != null && avg7prevWeight != null ? avg7Weight - avg7prevWeight : null;
  const slopePerDay = (() => {
    const n = weightPoints.length;
    if (n < 3) return null;
    const xs = weightPoints.map((_, i) => i);
    const xMean = xs.reduce((a, b) => a + b, 0) / n;
    const yMean = weightPoints.reduce((a, b) => a + b, 0) / n;
    let num = 0, den = 0;
    for (let i = 0; i < n; i++) {
      num += (xs[i] - xMean) * (weightPoints[i] - yMean);
      den += (xs[i] - xMean) ** 2;
    }
    return den ? num / den : null;
  })();
  const weeklyWeightDelta = meanCompareDelta ?? (slopePerDay != null ? slopePerDay * 7 : null);
  const weeklyDeltaSub = meanCompareDelta != null
    ? '7d avg vs prior 7d · scale truth'
    : weeklyWeightDelta != null
      ? `fitted from ${weightPoints.length}d of weigh-ins`
      : `need ≥3 weigh-ins (have ${weightPoints.length})`;

  // BF
  const bfPoints = data.recent.filter((e) => e.body_fat_pct != null).map((e) => e.body_fat_pct as number);
  const avg7BF = mean(bfPoints.slice(-7));

  // Waist
  // Intake
  const intakePoints7 = last7.filter((e) => e.calories_in != null);
  const dailyAvgIntake = mean(intakePoints7.map((e) => e.calories_in as number));

  // Burn — Mifflin BMR + WHOOP workouts + steps + NEAT, per day, then averaged.
  const computeDayBurn = (e: DailyEntry): number | null => {
    if (e.weight_kg == null) return null;
    const bmr = mifflinBMR({ weight_kg: e.weight_kg, height_cm: profile.height_cm, age: profile.age, sex: profile.sex });
    const stepCal = e.steps != null ? stepKcal(e.steps, e.weight_kg) : 0;
    const workoutCal = workoutKcalByDate[e.date] ?? 0;
    return bmr + stepCal + workoutCal + NEAT_BASELINE_KCAL;
  };
  const todayBurn = computeDayBurn(today);
  const burnPoints7 = last7.map(computeDayBurn).filter((v): v is number => v != null);
  const dailyAvgBurn = mean(burnPoints7);

  // Today's breakdown for the Burn tile sub.
  const todayBmr = today.weight_kg != null
    ? mifflinBMR({ weight_kg: today.weight_kg, height_cm: profile.height_cm, age: profile.age, sex: profile.sex })
    : null;
  const todayWorkoutKcal = workoutKcalByDate[today.date] ?? 0;
  const todayStepKcal = today.steps != null && today.weight_kg != null ? stepKcal(today.steps, today.weight_kg) : 0;

  // Energy balance (signed): intake - burn. Negative = deficit (losing). Positive = surplus.
  const todayBalance = todayBurn != null && today.calories_in != null ? today.calories_in - todayBurn : null;
  const weeklyTotalBalance = dailyAvgBurn != null && dailyAvgIntake != null ? (dailyAvgIntake - dailyAvgBurn) * 7 : null;
  const balanceTone = (b: number | null): Tone => b == null ? 'default' : b < -200 ? 'good' : b > 200 ? 'warn' : 'default';
  const fmtBalance = (b: number | null): string => {
    if (b == null) return '—';
    if (b === 0) return '0';
    return b < 0 ? `−${Math.abs(b).toFixed(0)}` : `+${b.toFixed(0)}`;
  };
  const balanceLabel = (b: number | null): string => b == null ? 'Balance' : b < 0 ? 'Deficit' : b > 0 ? 'Surplus' : 'Balanced';

  // Predicted daily weight change (signed: negative = lost). intake - burn over 7700 kcal/kg.
  const dailyWeightChangeKg = todayBalance != null ? todayBalance / KCAL_PER_KG : null;
  const weightChangeTone = (delta: number | null): Tone =>
    delta == null ? 'default' : delta < -0.005 ? 'good' : delta > 0.005 ? 'warn' : 'default';
  const fmtSignedKg = (delta: number | null): string => {
    if (delta == null) return '—';
    if (Math.abs(delta) < 0.005) return '0.00';
    return `${delta < 0 ? '−' : '+'}${Math.abs(delta).toFixed(2)}`;
  };

  // Trend label/tone for weight-tile sub
  const trendLabel = weeklyWeightDelta == null ? null
    : weeklyWeightDelta < -0.2 ? 'losing'
    : weeklyWeightDelta > 0.2 ? 'gaining'
    : 'stable';
  const weightSub = avg7Weight != null
    ? `7d avg ${avg7Weight.toFixed(2)}${weeklyWeightDelta != null ? ` · ${weeklyWeightDelta > 0 ? '+' : ''}${weeklyWeightDelta.toFixed(2)}/wk ${trendLabel}` : ''}`
    : 'awaiting weigh-ins';

  const hasExtras = today.hydration_pct != null || today.bone_mass_kg != null || today.visceral_fat != null || today.resting_hr != null;

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100 font-serif">
      <header className="border-b border-stone-800 px-6 py-4 relative">
        <div className="flex items-baseline justify-between">
          <h1 className="text-2xl font-medium tracking-tight">Tally</h1>
          <div className="flex items-center gap-4">
            <SyncLights syncing={syncing} lastSync={lastSync} />
            <button
              onClick={refresh}
              disabled={syncing}
              className="text-[11px] uppercase tracking-[0.2em] font-mono border border-stone-500 px-3 py-1.5 hover:border-stone-200 hover:text-stone-50 text-stone-200 disabled:opacity-70 disabled:cursor-wait transition-colors"
            >
              {syncing ? '↻ pulling latest…' : '↻ refresh latest data'}
            </button>
            <div className="text-xs font-mono text-stone-500">
              {BROWSER_BANGKOK_TODAY} · Tal
            </div>
          </div>
        </div>
        {syncing && (
          <div className="absolute bottom-0 left-0 right-0 h-0.5 overflow-hidden bg-stone-800">
            <div className="h-full w-1/3 bg-amber-300 animate-slide" />
          </div>
        )}
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-10">
        {/* --- Latest day at a glance --- */}
        <section>
          <SectionHead
            right={
              <button
                onClick={() => setEditOpen(!editOpen)}
                className="text-[10px] uppercase tracking-[0.2em] text-stone-400 hover:text-stone-100 font-mono"
              >
                {editLabel}
              </button>
            }
          >
            {headingLabel}
          </SectionHead>

          {editOpen && (
            <TodayEditor
              today={today}
              onSaved={() => { reload(); }}
              onClose={() => setEditOpen(false)}
            />
          )}

          {/* --- HERO row 1: calorie balance for the displayed (completed) day --- */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <Stat
              size="large"
              label="Burn"
              value={fmtNum(todayBurn)}
              unit="kcal"
              sub={
                todayBmr != null ? (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    <BurnPill tone="bmr" label="BMR" value={fmtNum(todayBmr)} />
                    <BurnPill tone="workouts" label="workouts" value={fmtNum(todayWorkoutKcal)} />
                    <BurnPill tone="ambient" label="steps + NEAT" value={fmtNum(todayStepKcal + NEAT_BASELINE_KCAL)} />
                    {dailyAvgBurn != null && (
                      <BurnPill tone="muted" label="7d avg" value={fmtNum(dailyAvgBurn)} />
                    )}
                  </div>
                ) : 'awaiting weight'
              }
            />
            <Stat
              size="large"
              label="Intake"
              value={today.calories_in?.toLocaleString() ?? '—'}
              unit="kcal"
              sub={dailyAvgIntake != null ? `7d avg ${fmtNum(dailyAvgIntake)}/day` : 'awaiting cronometer'}
            />
            <Stat
              size="large"
              label={balanceLabel(todayBalance)}
              value={fmtBalance(todayBalance)}
              unit="kcal"
              tone={balanceTone(todayBalance)}
              sub={weeklyTotalBalance != null
                ? `7d total ${fmtBalance(weeklyTotalBalance)} kcal · ${balanceLabel(weeklyTotalBalance).toLowerCase()}`
                : '—'}
            />
            <Stat
              size="large"
              label="Weight Δ (predicted)"
              value={fmtSignedKg(dailyWeightChangeKg)}
              unit="kg"
              tone={weightChangeTone(dailyWeightChangeKg)}
              sub="from this day's deficit"
            />
          </div>

          {/* --- HERO row 2: body composition + weekly delta --- */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
            <Stat
              size="large"
              tone="feature"
              label="Weight"
              value={today.weight_kg != null ? today.weight_kg.toFixed(2) : '—'}
              unit="kg"
              sub={weightSub}
            />
            <Stat
              size="large"
              tone="feature-bf"
              label="Body Fat"
              value={fmtNum(today.body_fat_pct, 1)}
              unit="%"
              sub={avg7BF != null ? `7d avg ${avg7BF.toFixed(1)}%` : '—'}
            />
            <Stat
              size="large"
              label="Weight Δ (actual)"
              value={fmtSignedKg(weeklyWeightDelta)}
              unit="kg"
              tone={weightChangeTone(weeklyWeightDelta)}
              sub={weeklyDeltaSub}
            />
          </div>

          {/* --- HERO row 3: tape measurements --- */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
            {MEASUREMENT_METRICS.map(({ metric, label }) => (
              <HeroMeasurement
                key={metric}
                metric={metric}
                label={label}
                date={BROWSER_BANGKOK_TODAY}
                initial={latestByMetric(data.measurements, metric)}
                onSaved={reload}
              />
            ))}
          </div>

          {/* --- Secondary row: muscle / sleep / recovery / HRV --- */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-8">
            <Stat
              label="Muscle Mass"
              value={fmtNum(today.muscle_mass_kg, 1)}
              unit="kg"
            />
            <Stat
              label="Sleep"
              value={sleepDisplay}
            />
            <Stat
              label="Recovery"
              value={fmtNum(today.whoop_recovery)}
              unit="%"
              tone={recoveryTone}
            />
            <Stat
              label="HRV"
              value={today.hrv_ms != null ? today.hrv_ms.toFixed(0) : '—'}
              unit="ms"
            />
          </div>

          {hasExtras && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-6">
              <Stat
                label="Hydration"
                value={fmtNum(today.hydration_pct, 1)}
                unit="%"
              />
              <Stat
                label="Bone Mass"
                value={fmtNum(today.bone_mass_kg, 2)}
                unit="kg"
              />
              <Stat
                label="Visceral Fat"
                value={fmtNum(today.visceral_fat, 1)}
              />
              <Stat
                label="Resting HR"
                value={fmtNum(today.resting_hr)}
                unit="bpm"
              />
            </div>
          )}

          {/* --- Today (in-progress) — only if we're displaying yesterday as the completed day --- */}
          {!isToday && actualToday && (() => {
            const aBurn = (actualToday.weight_kg != null) ? (
              mifflinBMR({ weight_kg: actualToday.weight_kg, height_cm: profile.height_cm, age: profile.age, sex: profile.sex })
              + (actualToday.steps != null ? stepKcal(actualToday.steps, actualToday.weight_kg) : 0)
              + (workoutKcalByDate[actualToday.date] ?? 0)
              + NEAT_BASELINE_KCAL
            ) : null;
            const aIntake = actualToday.calories_in;
            const aBalance = aBurn != null && aIntake != null ? aIntake - aBurn : null;
            return (
              <div className="mt-12 pt-6 border-t border-stone-800">
                <div className="text-[10px] uppercase tracking-[0.25em] text-stone-500 font-mono mb-4">
                  Today (in progress) · {actualToday.date}
                </div>
                {/* Highlighted: fresh Withings readings from this morning */}
                <div className="grid grid-cols-2 md:grid-cols-2 gap-6 mb-6">
                  <HighlightedStat
                    label="Weight"
                    value={actualToday.weight_kg != null ? actualToday.weight_kg.toFixed(2) : '—'}
                    unit="kg"
                    sub="from this morning"
                  />
                  <HighlightedStat
                    label="Body Fat"
                    value={actualToday.body_fat_pct != null ? actualToday.body_fat_pct.toFixed(1) : '—'}
                    unit="%"
                    sub="from this morning"
                  />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                  <Stat
                    label="Intake so far"
                    value={aIntake != null ? aIntake.toLocaleString() : '—'}
                    unit="kcal"
                    sub={aIntake == null ? 'awaiting cronometer log' : undefined}
                  />
                  <Stat
                    label="Burn so far"
                    value={aBurn != null ? Math.round(aBurn).toLocaleString() : '—'}
                    unit="kcal"
                    sub="bmr + workouts so far + neat"
                  />
                  <Stat
                    label={`${balanceLabel(aBalance)} so far`}
                    value={fmtBalance(aBalance)}
                    unit="kcal"
                    tone={balanceTone(aBalance)}
                    sub={aBalance == null ? 'needs intake + weight' : undefined}
                  />
                </div>
              </div>
            );
          })()}
        </section>

        {/* --- Learned BMR — back-derived from logged data, gets smarter over time --- */}
        <LearnedBMR today={today} recent={data.recent} profile={profile} workoutKcalByDate={workoutKcalByDate} />

        {/* --- Reality + Deficit (derived from intake + scale; the headline numbers) --- */}
        <section>
          <SectionHead>Energy balance</SectionHead>
          <Reality today={today} recent={data.recent} reconciliation={recon} profile={profile} workoutKcalByDate={workoutKcalByDate} />
        </section>

        {/* --- Trends --- */}
        <section>
          <SectionHead>Trends · 21 days</SectionHead>
          <div className="grid md:grid-cols-2 gap-6">
            <ChartCard title="Weight · kg" dataKey="weight" data={chartData} color="#e7e5e4" domain={['dataMin - 0.5', 'dataMax + 0.5']} labelDigits={2} />
            <ChartCard title="Body Fat · %" dataKey="bf" data={chartData} color="#c4b5fd" domain={['dataMin - 0.3', 'dataMax + 0.3']} labelDigits={1} />
            {MEASUREMENT_METRICS.map(({ metric, label, color }) => (
              <MeasurementChartCard
                key={metric}
                title={label}
                color={color}
                log={filterByMetric(data.measurements, metric)}
              />
            ))}
            <DualChart title="Intake vs WHOOP burn · kcal" data={chartData} />
          </div>
        </section>

        {/* --- Cronometer fallback if scraper is stale --- */}
        {cronometerStatus && (cronometerStatus.status === 'stale' || cronometerStatus.status === 'error') && (
          <CronometerFallback onDone={reload} />
        )}

        {/* --- Sources --- */}
        <section>
          <SectionHead>Sources</SectionHead>
          <div className="grid md:grid-cols-4 gap-4 text-sm">
            <SourceCard name="WHOOP" sync={whoopStatus} items={['workouts', 'strain', 'sleep', 'kcal']} />
            <SourceCard name="Withings" sync={withingsStatus} items={['weight', 'body fat %', 'muscle mass']} />
            <SourceCard name="Cronometer" sync={cronometerStatus} items={['intake', 'macros', 'micros']} />
            <SourceCard name="Tape" items={['waist (above navel)', 'chest (nipple line)', 'shoulders (widest)']}>
              <div className="flex flex-col gap-2">
                {MEASUREMENT_METRICS.map(({ metric, label }) => (
                  <MeasurementInline
                    key={metric}
                    metric={metric}
                    label={label}
                    date={today.date}
                    initial={latestByMetric(data.measurements, metric)}
                    onSaved={reload}
                  />
                ))}
              </div>
            </SourceCard>
          </div>
          <p className="text-xs text-stone-500 font-mono mt-4">
            OAuth: connect at <span className="text-stone-300">/oauth/whoop/start</span> and{' '}
            <span className="text-stone-300">/oauth/withings/start</span>. Cronometer pulls automatically; fall back to paste-CSV
            when it breaks.
          </p>
        </section>
      </div>

      <footer className="border-t border-stone-800 mt-16 px-6 py-4 text-[10px] uppercase tracking-[0.25em] text-stone-600 font-mono">
        tally · v0.1
      </footer>
    </div>
  );
}

/**
 * Three-light pull confirmation. After the user clicks "refresh latest data" each source
 * (WHOOP, Withings, Cronometer) lights up green on success, red on error, amber on stale.
 * While the sync is in flight, all three pulse amber so it's obvious work is happening.
 */
function SyncLights({ syncing, lastSync }: { syncing: boolean; lastSync: SyncSummary | null }) {
  type SourceStatus = 'idle' | 'pending' | 'ok' | 'stale' | 'error';
  const status = (key: 'whoop' | 'withings' | 'cronometer'): SourceStatus => {
    if (syncing) return 'pending';
    if (!lastSync) return 'idle';
    const v = lastSync[key];
    if (v === 'ok') return 'ok';
    if (v === 'stale') return 'stale';
    if (v === 'skipped') return 'idle';
    return 'error';
  };
  return (
    <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em]">
      <SyncLight label="whoop" status={status('whoop')} />
      <SyncLight label="withings" status={status('withings')} />
      <SyncLight label="cron" status={status('cronometer')} />
    </div>
  );
}

function SyncLight({ label, status }: { label: string; status: 'idle' | 'pending' | 'ok' | 'stale' | 'error' }) {
  const dotClass = {
    idle: 'bg-stone-700',
    pending: 'bg-amber-300 animate-pulse shadow-[0_0_8px_rgba(252,211,77,0.7)]',
    ok: 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.7)]',
    stale: 'bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.6)]',
    error: 'bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.7)]',
  }[status];
  const textClass = {
    idle: 'text-stone-600',
    pending: 'text-amber-300',
    ok: 'text-emerald-300',
    stale: 'text-amber-300',
    error: 'text-rose-300',
  }[status];
  return (
    <span className={`inline-flex items-center gap-1.5 border border-stone-700 px-2 py-1 ${textClass}`}>
      <span className={`inline-block w-2 h-2 rounded-full ${dotClass}`} />
      {label}
    </span>
  );
}

function BurnPill({
  tone,
  label,
  value,
}: {
  tone: 'bmr' | 'workouts' | 'ambient' | 'muted';
  label: string;
  value: string;
}) {
  const toneClass: Record<typeof tone, string> = {
    bmr: 'border-stone-500 text-stone-200',
    workouts: 'border-rose-700/70 text-rose-300',
    ambient: 'border-amber-600/70 text-amber-300',
    muted: 'border-stone-800 text-stone-500',
  };
  return (
    <span
      className={`inline-flex items-baseline gap-1.5 px-2 py-0.5 border rounded-full text-[10px] font-mono uppercase tracking-wider ${toneClass[tone]}`}
    >
      <span className="opacity-70">{label}</span>
      <span className="font-medium tabular-nums">{value}</span>
    </span>
  );
}
